import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryCard from '../../components/obituary/ObituaryCard';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ErrorState from '../../components/common/ErrorState';
import { CardGridSkeleton } from '../../components/common/Skeleton';
import { useToast } from '../../context/ToastContext';
import { fetchObituaries } from '../../utils/api';
import { getDaysUntil } from '../../components/obituary/obituaryUtils';

/**
 * ObituaryListPage - 부고 목록 페이지
 * 모든 부고를 카드 형태로 표시하며 검색/정렬/필터 기능 제공
 */
const ObituaryListPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [obituaries, setObituaries] = useState([]);
  const [searchInput, setSearchInput] = useState('');   // 입력값
  const [searchTerm, setSearchTerm] = useState('');     // 디바운스된 검색어
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [sortBy, setSortBy] = useState('latest'); // 'latest' | 'name' | 'departure'
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'upcoming' | 'favorite'

  // 부고 목록 불러오기
  const loadObituaries = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      const data = await fetchObituaries();
      setObituaries(data);
    } catch (error) {
      console.error('Failed to load obituaries:', error);
      setIsError(true);
      showToast('부고 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadObituaries();
  }, [loadObituaries]);

  // 검색어 디바운스 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // 필터 + 검색 + 정렬을 파생값으로 계산 (별도 state 동기화 제거)
  const displayedObituaries = useMemo(() => {
    let result = [...obituaries];

    // 필터
    if (filterMode === 'upcoming') {
      result = result.filter((o) => {
        const d = getDaysUntil(o.departureDate);
        return d !== null && d >= 0;
      });
    } else if (filterMode === 'favorite') {
      result = result.filter((o) => o.isFavorite);
    }

    // 검색
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          o.deceasedName.toLowerCase().includes(q) ||
          o.funeralHome.toLowerCase().includes(q) ||
          o.chiefMourner.toLowerCase().includes(q)
      );
    }

    // 정렬
    result.sort((a, b) => {
      if (sortBy === 'latest') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      if (sortBy === 'name') {
        return a.deceasedName.localeCompare(b.deceasedName, 'ko');
      }
      if (sortBy === 'departure') {
        return new Date(a.departureDate) - new Date(b.departureDate);
      }
      return 0;
    });

    return result;
  }, [obituaries, filterMode, searchTerm, sortBy]);

  // 검색 초기화
  const handleClearSearch = () => setSearchInput('');

  // 즐겨찾기 토글
  const handleToggleFavorite = (id) => {
    setObituaries((prev) =>
      prev.map((obit) =>
        obit.id === id ? { ...obit, isFavorite: !obit.isFavorite } : obit
      )
    );
  };

  const handleCreateObituary = () => navigate('/obituaries/create');
  const handleViewDetail = (obituaryId) => navigate(`/obituaries/${obituaryId}`);
  const handleSendWreath = (obituaryId, obituary) =>
    navigate('/wreaths', { state: { obituaryId, obituary } });

  const filterTabs = [
    { key: 'all', label: '전체' },
    { key: 'upcoming', label: '발인 예정' },
    { key: 'favorite', label: '즐겨찾기' },
  ];

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-900 mb-2">부고 목록</h1>
              <p className="text-gray-600" aria-live="polite">
                {isLoading
                  ? '부고를 불러오는 중입니다...'
                  : `총 ${displayedObituaries.length}개의 부고가 있습니다.`}
              </p>
            </div>
            <Button
              variant="primary"
              onClick={handleCreateObituary}
              className="flex items-center gap-2"
            >
              <Plus size={20} />
              부고 작성
            </Button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-4 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Input
                type="text"
                placeholder="고인명, 장례식장, 상주명으로 검색..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="정렬 기준"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="latest">최신순</option>
              <option value="name">이름순</option>
              <option value="departure">발인 임박순</option>
            </select>

            {searchInput && (
              <Button variant="outline" onClick={handleClearSearch} className="whitespace-nowrap">
                검색 초기화
              </Button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2" role="tablist" aria-label="부고 필터">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={filterMode === tab.key}
              onClick={() => setFilterMode(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filterMode === tab.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-accent-100 text-accent-700 hover:bg-accent-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading ? (
          <CardGridSkeleton count={6} columns={3} withImage={false} />
        ) : isError ? (
          <ErrorState
            title="부고 목록을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
            onRetry={loadObituaries}
          />
        ) : displayedObituaries.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg mb-4">
              {searchTerm || filterMode !== 'all'
                ? '조건에 맞는 부고가 없습니다.'
                : '등록된 부고가 없습니다.'}
            </p>
            {!searchTerm && filterMode === 'all' && (
              <Button
                variant="primary"
                onClick={handleCreateObituary}
                className="inline-flex items-center gap-2"
              >
                <Plus size={20} />
                첫 부고 작성하기
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedObituaries.map((obituary) => (
              <ObituaryCard
                key={obituary.id}
                obituary={obituary}
                onFavoriteToggle={handleToggleFavorite}
                onViewDetail={() => handleViewDetail(obituary.id)}
                onSendWreath={(id, obit) => handleSendWreath(id, obit)}
              />
            ))}
          </div>
        )}

        {/* Info */}
        {!isLoading && !isError && displayedObituaries.length > 0 && (
          <div className="mt-8 p-4 bg-accent-50 rounded-lg border border-accent-200">
            <p className="text-sm text-gray-700 text-center">
              <span className="font-semibold">안내:</span> 부고를 클릭하면 상세 정보를 확인할 수 있습니다.
            </p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ObituaryListPage;
